import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './PublicInvoice.css';

const PublicInvoice = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [tipInput, setTipInput] = useState('');
  const success = searchParams.get('success') === 'true';

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await fetch(`/api/invoices/public/${token}`);
        if (!res.ok) throw new Error('Invoice not found');
        const data = await res.json();
        setInvoice(data);
      } catch (err) {
        setError('This invoice could not be found.');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [token]);

  const tipAmount = parseFloat(tipInput) || 0;
  const chargeTotal = parseFloat(invoice?.total || 0) + tipAmount;

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const res = await fetch(`/api/stripe/create-checkout-session/${invoice.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipAmount }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to start payment. Please try again.');
        setPaying(false);
      }
    } catch (err) {
      alert('Failed to start payment. Please try again.');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="public-invoice-page">
        <div className="public-invoice-loading">Loading invoice...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-invoice-page">
        <div className="public-invoice-error">{error}</div>
      </div>
    );
  }

  const isQuote = !!invoice.is_quote;
  const isPaid = invoice.status === 'paid';
  const formattedDate = invoice.created_at
    ? new Date(invoice.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const formattedDue = invoice.due_date
    ? new Date(invoice.due_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="public-invoice-page">
      <div className="public-invoice-card">

        {/* Header */}
        <div className="public-invoice-header">
          <img
            src={process.env.REACT_APP_LOGO_URL || '/assets/polaris-logo.png'}
            alt="Logo"
            className="public-invoice-logo"
          />
          <div className="public-invoice-title">
            <h1>{isQuote ? 'Quote' : 'Invoice'} #{invoice.invoice_number}</h1>
            <p>Issued {formattedDate}</p>
            {!isQuote && formattedDue && <p>Due {formattedDue}</p>}
          </div>
          {!isQuote && (
            <div className={`public-invoice-badge ${isPaid ? 'paid' : 'unpaid'}`}>
              {isPaid ? 'Paid' : 'Unpaid'}
            </div>
          )}
        </div>

        {/* Success Banner */}
        {success && isPaid && (
          <div className="public-invoice-success">
            Payment received — thank you! A confirmation will be sent to your email.
          </div>
        )}

        {/* Billed To */}
        <div className="public-invoice-section">
          <h2>Billed To</h2>
          <p className="public-invoice-customer">
            {invoice.first_name} {invoice.last_name}
          </p>
          {invoice.email && <p>{invoice.email}</p>}
          {invoice.phone && <p>{invoice.phone}</p>}
          {invoice.address && (
            <p>{invoice.address}{invoice.city ? `, ${invoice.city}` : ''}{invoice.state ? `, ${invoice.state}` : ''} {invoice.zip || ''}</p>
          )}
        </div>

        {/* Line Items */}
        <div className="public-invoice-section">
          <h2>Services</h2>
          <table className="public-invoice-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items && invoice.items.map((item, i) => (
                <tr key={i}>
                  <td>{item.description}</td>
                  <td>{item.quantity}</td>
                  <td>${parseFloat(item.unit_price).toFixed(2)}</td>
                  <td>${parseFloat(item.total).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="public-invoice-totals">
          <div className="public-invoice-totals-row">
            <span>Subtotal</span>
            <span>${parseFloat(invoice.subtotal || 0).toFixed(2)}</span>
          </div>
          {invoice.tax_rate > 0 && (
            <div className="public-invoice-totals-row">
              <span>Tax ({(invoice.tax_rate * 100).toFixed(2)}%)</span>
              <span>${parseFloat(invoice.tax_amount || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="public-invoice-totals-row total">
            <span>{isQuote ? 'Estimated Total' : 'Total'}</span>
            <span>${parseFloat(invoice.total || 0).toFixed(2)}</span>
          </div>

          {!isQuote && !isPaid && (
            <div className="public-invoice-tip">
              {!showTip ? (
                <button className="public-invoice-tip-toggle" onClick={() => setShowTip(true)}>
                  + Add a tip
                </button>
              ) : (
                <div className="public-invoice-tip-open">
                  <div className="public-invoice-tip-row">
                    <label htmlFor="tip-input">Tip</label>
                    <div className="public-invoice-tip-input-wrap">
                      <span className="public-invoice-tip-dollar">$</span>
                      <input
                        id="tip-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={tipInput}
                        onChange={e => setTipInput(e.target.value)}
                        className="public-invoice-tip-input"
                        autoFocus
                      />
                    </div>
                  </div>
                  <button className="public-invoice-tip-remove" onClick={() => { setShowTip(false); setTipInput(''); }}>
                    — Remove tip
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="public-invoice-section public-invoice-notes">
            <h2>A note from us:</h2>
            <p>{invoice.notes}</p>
          </div>
        )}

        {/* Pay Button */}
        {!isQuote && !isPaid && (
          <div className="public-invoice-pay">
            <button
              className="public-invoice-pay-btn"
              onClick={handlePayNow}
              disabled={paying}
            >
              {paying ? 'Redirecting to payment...' : `Pay $${chargeTotal.toFixed(2)} Now`}
            </button>
            <p className="public-invoice-secure">Secured by Stripe</p>
          </div>
        )}

        {!isQuote && isPaid && (
          <div className="public-invoice-paid-footer">
            This invoice has been paid. Thank you for your business!
          </div>
        )}

        {isQuote && (
          <div className="public-invoice-paid-footer">
            This is an estimate — pricing may vary based on vehicle condition. Ready to book? Reply to this text or give us a call!
          </div>
        )}

        {/* Footer */}
        <div className="public-invoice-footer">
          <p>Polaris Pro Mobile Detailing</p>
          {process.env.REACT_APP_BUSINESS_TAGLINE && (
            <p>{process.env.REACT_APP_BUSINESS_TAGLINE}</p>
          )}
        </div>

      </div>
    </div>
  );
};

export default PublicInvoice;
